/** @typedef {string | number} Storable */

/**
 * @template {Storable} T
 * @param {string} key
 * @param {T} value
 * @param {StoreOptions} options
 * @
 */
function store(key, value, {
  duration
} = {}) {
  const ttl = duration ? Date.now() + duration : undefined;
  localStorage.setItem(key, JSON.stringify({
    value,
    ...(ttl ? {
      ttl
    } : {})
  }));
}

/**
 * @template {Storable} T
 * @returns {T | null}
 */
function retrieve(key) {
  const json = localStorage.getItem(key);
  const stored = json && JSON.parse(json);
  if (stored) {
    if (!stored.ttl || stored.ttl > Date.now()) {
      return stored.value;
    }
    localStorage.removeItem(key);
  }
  return null;
}

/**
 * @typedef {object} StoreOptions
 * @property {number} duration - in milliseconds
 */

/**
 * @template {Storable} T
 * @typedef {object} Storage
 * @property {(value: T, options?: StoreOptions) => void} store
 * @property {() => T | null} retrieve
 */

/**
 * @template {Storable} T
 * @param {string} key
 * @param {StoreOptions} [defaultOptions]
 * @returns {Storage<T>}
 */
function of(key, defaultOptions) {
  return {
    store: (value, options) => store(key, value, {
      ...defaultOptions,
      ...options
    }),
    retrieve: () => retrieve(key)
  };
}

export { of, retrieve, store };
